"use client";

import { Button } from "@/components/primitives/Button";
import { ConsentPrivacyLabel } from "@/components/forms/ConsentPrivacyLabel";
import { getFormsCopy } from "@/content/forms";
import {
  Form,
  FormCheckbox,
  FormInput,
  FormStatus,
  FormTextarea,
  useFormStatus,
} from "@/components/primitives/Form";
import { submitContact } from "@/lib/api/endpoints";
import { buildFormConsentRecord } from "@/lib/consent";
import type { Locale } from "@/lib/i18n/config";
import { useLocale } from "@/lib/i18n/provider";
import { contactInputSchema } from "@/lib/api/types";
import { withAttribution } from "@/lib/utm";

export function ContactForm({ className = "" }: { className?: string }) {
  const { locale } = useLocale();
  const forms = getFormsCopy(locale);

  return (
    <Form
      schema={contactInputSchema}
      trackingName="contact"
      successMessage={forms.contact.successMessage}
      className={className}
      onSubmit={async (values, signal) => {
        const { consentGiven, ...input } = values;
        if (!consentGiven) return;

        const consentRecord = buildFormConsentRecord({
          consentText: forms.contact.consentTextRecord,
          locale,
          formSource: "contact",
        });

        const attributed = withAttribution(
          { ...input },
          { entryPoint: "contact", sourceBase: "contact" },
        );

        const result = await submitContact(attributed, consentRecord, signal);
        if (result.ticketId) return forms.contact.ticketSuccessMessage;
      }}
    >
      <div className="flex flex-col gap-3">
        <FormInput
          name="name"
          label={forms.contact.nameLabel}
          required
          placeholder={forms.contact.namePlaceholder}
          autoComplete="name"
        />
        <FormInput
          name="email"
          type="email"
          label={forms.contact.emailLabel}
          required
          placeholder={forms.contact.emailPlaceholder}
          autoComplete="email"
        />
        <FormInput
          name="phone"
          label={forms.contact.phoneLabel}
          placeholder={forms.contact.phonePlaceholder}
          autoComplete="tel"
        />
        <FormTextarea
          name="message"
          label={forms.contact.messageLabel}
          rows={4}
          placeholder={forms.contact.messagePlaceholder}
        />
        <FormCheckbox
          name="consentGiven"
          required
          label={<ConsentPrivacyLabel text={forms.contact.consentLabel} />}
        />
        <Submit locale={locale} />
        <FormStatus />
      </div>
    </Form>
  );
}

function Submit({ locale }: { locale: Locale }) {
  const forms = getFormsCopy(locale);
  const status = useFormStatus();
  const busy = status === "submitting";
  return (
    <Button type="submit" variant="primary" size="lg" disabled={busy}>
      {busy ? forms.contact.busyLabel : forms.contact.submitLabel}
    </Button>
  );
}
